import React from 'react';

interface SparklineProps {
  values: Array<number | null | undefined>;
  width?: number;
  height?: number;
  color?: string;
}

// Minimal sparkline using inline SVG (no external deps)
// Renders gaps for missing values and fixes y-axis min to 0.9*min(series) when > 0 (avoids flat look)
const Sparkline: React.FC<SparklineProps> = ({ values, width = 120, height = 28, color = '#4f46e5' }) => {
  const nums = values.map(v => (v == null || isNaN(Number(v)) ? null : Number(v)));
  const present = nums.filter((v): v is number => v !== null);
  if (present.length === 0) {
    return (<svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="sparkline" />);
  }
  const rawMin = Math.min(...present);
  const rawMax = Math.max(...present);
  const yMin = rawMin > 0 ? rawMin * 0.9 : rawMin; // fixed min scaling when values far from 0
  const yMax = rawMax;
  const range = Math.max(yMax - yMin, 1e-6);
  const step = nums.length > 1 ? width / (nums.length - 1) : width;

  // Build contiguous segments (gaps where values are null)
  const segments: Array<Array<string>> = [];
  let current: Array<string> = [];
  nums.forEach((v, i) => {
    const x = i * step;
    if (v === null) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
      return;
    }
    const y = height - ((v - yMin) / range) * height;
    current.push(`${x},${y}`);
  });
  if (current.length > 0) segments.push(current);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="sparkline">
      {segments.map((pts, idx) => (
        <polyline key={idx} fill="none" stroke={color} strokeWidth={2} points={pts.join(' ')} />
      ))}
    </svg>
  );
};

export default Sparkline;
